'use client';
import React from 'react';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import Image from '@repo/ui/src/components/Image';
import SaveButton from '@views/RecipeDetailPage/_SaveButton';
import PrintButton from '@views/RecipeDetailPage/_PrintButton';
import RecipeCardInfo from '@views/RecipeDetailPage/_RecipeCardInfo';
import ShareIcon from '@repo/ui/src/components/Icons/ShareIcon';
import Tooltip from '@components/Tooltip';
import ExpandableText from '@repo/ui/src/components/ExpandableText';
import RatingStars from '@repo/ui/src/components/RatingStars';
import { recipeShared } from '@repo/ui/src/services/analytics';
import { SegmentRecipeProps } from '@components/RecipeNotes/_RecipeNote';
import {
  IngredientSummaryProps,
  MetaProps,
  RecipeMetaSectionProps,
  RecipeMigrationProps,
  RecipeStepProps,
} from '@repo/ui/src/types';

export interface RecipeTimeProps {
  label: string;
  value: number;
}
interface Props {
  heroImage: string | null;
  recipeMetaSection: RecipeMetaSectionProps;
  recipeMigrationSection: RecipeMigrationProps;
  ratingSummary: {
    average: number;
    count: number;
  };
  servingSize: number;
  authorName: string;
  _meta: MetaProps;
  prepTime: Array<RecipeTimeProps>;
  cookTime: Array<RecipeTimeProps>;
  steps?: Array<RecipeStepProps>;
  ingredientSummary?: IngredientSummaryProps;
  unitOfMeasure: number;
  // setTabIndex: (value: number) => void;
  _testID: string;
  segmentRecipe: SegmentRecipeProps;
}

const RecipeHero = ({
  heroImage,
  recipeMetaSection,
  recipeMigrationSection,
  ratingSummary,
  servingSize,
  authorName,
  prepTime,
  cookTime,
  steps,
  ingredientSummary,
  unitOfMeasure,
  // setTabIndex,
  _testID,
  _meta,
  segmentRecipe,
}: Props) => {
  const [tooltipShow, setTooltipShow] = React.useState('');
  const ref = React.useRef<number>();
  const { t } = useTranslation();
  const url = `https://traeger.com/${_meta?.deliveryKey}`;

  const openTooltip = React.useCallback(
    (text: string) => {
      setTooltipShow(text);
      window.clearTimeout(ref.current);
      ref.current = window.setTimeout(() => {
        setTooltipShow('');
      }, 2000);
    },
    [ref],
  );

  const share = React.useCallback(() => {
    openTooltip(t('recipeContent.linkCopied', undefined, { default: 'Link Copied' }));
    recipeShared({
      ...segmentRecipe,
      recipe_id: _meta?.deliveryId ?? '',
      featured_products: segmentRecipe.featuredProducts ?? [],
    });
  }, [openTooltip, t]);

  return (
    <div className="w-full md:grid md:grid-cols-[5fr_4fr] gap-6 lg:gap-36 sm:pt-[82px]">
      <div className="relative aspect-video w-full md:aspect-auto md:h-[525px] md:mt-6">
        <div className="absolute pt-4 z-10 right-6 sm:hidden">
          <CopyToClipboard text={url} onCopy={share}>
            <button className="flex justify-center relative">
              <ShareIcon className="cursor-pointer flex" color="#ffff" />
              <Tooltip
                visible={!!tooltipShow}
                className="bg-neutral-600 text-neutral-100 after:border-b-neutral-600"
                below
              >
                {tooltipShow}
              </Tooltip>
            </button>
          </CopyToClipboard>
        </div>
        {heroImage && (
          <Image src={heroImage} alt={recipeMetaSection?.title} objectFit="cover" layout="fill" />
        )}
      </div>
      <div className="w-full px-6 sm:px-0 sm:w-auto">
        <div>
          <div className="hidden sm:flex gap-2">
            <div className="relative pt-5 md:pt-0">
              <CopyToClipboard text={url} onCopy={share}>
                <button className="flex justify-center relative bg-warm-300 rounded-full hover:bg-brand-300 hover:text-warm-100 focus:text-brand-400">
                  <ShareIcon className="cursor-pointer flex w-10" />
                  <Tooltip
                    visible={!!tooltipShow}
                    className="bg-neutral-600 after:border-b-neutral-600 text-neutral-100"
                    below
                  >
                    {tooltipShow}
                  </Tooltip>
                </button>
              </CopyToClipboard>
            </div>
            <PrintButton
              variant="icon"
              servingSize={servingSize}
              authorName={authorName}
              ingredientSummary={ingredientSummary}
              unitOfMeasure={unitOfMeasure}
              recipeMetaSection={recipeMetaSection}
              prepTime={prepTime}
              cookTime={cookTime}
              steps={steps}
              segmentRecipe={segmentRecipe}
            />
          </div>
          <hr className="h-1 w-12 bg-brand-300 hidden sm:block my-6" />
          <h1 className="tra-text-web-heading-3 font-normal pt-6 md:pt-0">
            {recipeMetaSection?.title}
          </h1>
        </div>
        <p className="tra-text-web-body-s pt-2 sm:pt-4">By {authorName}</p>
        <div className="flex row mt-4">
          {!!ratingSummary?.average && recipeMigrationSection ? (
            <div className="pb-4">
              <RatingStars value={ratingSummary.average} reviewCount={ratingSummary.count} />
            </div>
          ) : null}
        </div>
        {!!recipeMetaSection?.description && (
          <ExpandableText
            text={recipeMetaSection.description}
            collapsedLines={3}
            showMoreText={t('recipeContent.showMore', undefined, { default: 'Show More' })}
            showLessText={t('recipeContent.showLess', undefined, { default: 'Show Less' })}
            className="text-neutral-600"
          />
        )}
        <div className="pb-10 flex mt-4 flex-wrap gap-4 sm:gap-2 lg:gap-4">
          {!!_meta.deliveryId && (
            <SaveButton
              contentId={_meta.deliveryId}
              className="basis-auto min-w-52 flex-grow lg:flex-1"
            />
          )}
        </div>
        <RecipeCardInfo
          recipeMetaSection={recipeMetaSection}
          _testID={_testID}
          prepTime={prepTime}
          cookTime={cookTime}
        />
      </div>
    </div>
  );
};
export default RecipeHero;
